import { Dispatch, SetStateAction, useRef, useState } from "react";
import Button from "../../../component/Button";
import { useSearchParams } from "react-router-dom";
import Checkbox from "../../../component/CheckBox";

import { TermsPopUp } from "../../terms/TermsPopUp";
import { useResponsiveMobile } from "../../../hooks/useResponsiveMobile";
import { useModalStore } from "../../../store/modalStore";

type Errors = {
  company?: string;
  representative?: string;
  department?: string;
  email?: string;
  phone?: string;
  agree?: string;
};
type InputFormProps = {
  label: string;
  value: string;
  placeholder?: string; // 이제는 실제 placeholder 문구로 쓰기보단 접근성/힌트용 선택
  inputRef?: React.RefObject<HTMLInputElement | null>;
  error?: boolean;
  errorText?: string;
  onChange: (v: string) => void;
  type?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  autoComplete?: string;
  name?: string;
  id?: string;
  limitLength?: boolean;
};

export const InputForm = function InputForm({
  label,
  value,
  inputRef,
  error,
  errorText,
  onChange,
  type = "text",
  inputMode,
  autoComplete,
  name,
  id,
  limitLength,
}: InputFormProps) {
  const inputId = id ?? name ?? label.replace(/\s+/g, "_");

  const describedBy = error ? `${inputId}-error` : undefined;

  return (
    <div className="flex flex-col gap-2">
      <div
        className={[
          "relative rounded-lg border-[1px] bg-white px-4 pt-7 pb-3",
          "focus-within:border-[#0079FF]", //  추가/유지
          error ? "border-[#F13221] focus-within:border-[#F13221]" : "border-[#DDE0E6]",
        ].join(" ")}
      >
        <input
          maxLength={limitLength ? 20 : undefined}
          id={inputId}
          name={name}
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          type={type}
          inputMode={inputMode}
          autoComplete={autoComplete}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          // 핵심: placeholder는 " " 로 두어 placeholder-shown 상태를 트리거
          placeholder=" "
          className={[
            "peer w-full border-none bg-transparent text-[16px] text-[#121319] outline-none ",
            // 위로 올라간 라벨 공간 확보(이미 pt/pb로 확보했지만, 더 여유 주고 싶으면)
            // "leading-6",
            "placeholder:text-transparent",
          ].join(" ")}
        />

        <label
          htmlFor={inputId}
          className={[
            "pointer-events-none absolute left-4",
            "text-[#4D4F58]",
            // 애니메이션
            "transition-all duration-200 ease-out",
            // 기본(값 없고 포커스 없을 때): 가운데 placeholder처럼
            "top-1/2 -translate-y-1/2 text-[16px]",
            // 포커스 또는 값이 있으면: 위로 + 작게
            "peer-focus:top-2 peer-focus:translate-y-0 peer-focus:text-[12px]",
            // 값이 생기면(placeholder-shown이 아니면): 위로 + 작게 유지
            "peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[12px]",
            // 에러 시 라벨 색 변경(원하면)
            error ? "text-[#F13221]" : "",
          ].join(" ")}
        >
          {label}
        </label>
      </div>

      {error && !!errorText && (
        <span
          id={`${inputId}-error`}
          className="text-[12px] leading-[16px]"
          style={{ color: "#F13221" }}
        >
          {errorText}
        </span>
      )}
    </div>
  );
};

const trim = (v: string) => v.trim();

export const StepOne = ({
  setStep,
  form,
  setForm,
}: {
  setStep: (step: number) => void;
  form: {
    company: string;
    representative: string;
    department: string;
    email: string;
    phone: string;
  };
  setForm: Dispatch<
    SetStateAction<{
      company: string;
      representative: string;
      department: string;
      email: string;
      phone: string;
    }>
  >;
}) => {
  const openModal = useModalStore((s) => s.open);
  const isMobile = useResponsiveMobile(900);
  const [onTerms, setOnTerms] = useState(false);
  const onlyDigits = (v: string) => v.replace(/\D/g, "");

  const isValid010Mobile = (phone: string) => {
    // 010 + 숫자 8자리 = 총 11자리
    return /^010\d{8}$/.test(phone);
  };

  const isValidEmail = (email: string) => {
    const v = email.trim();
    // “현업 보편형” (너무 엄격하지 않게)
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
  };

  const isValidKoreanMobile = (phoneRaw: string) => {
    const d = onlyDigits(phoneRaw);
    // 01로 시작 + 10~11자리 (국내 휴대전화/일부 011~019 포함)
    if (!/^01\d{8,9}$/.test(d)) return false;
    return true;
  };

  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");

  const [agree, setAgree] = useState(false);

  const [errors, setErrors] = useState<Errors>({});
  const hasNumber = (v: string) => /\d/.test(v);
  const companyRef = useRef<HTMLInputElement | null>(null);
  const representativeRef = useRef<HTMLInputElement | null>(null);
  const departmentRef = useRef<HTMLInputElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const phoneRef = useRef<HTMLInputElement | null>(null);
  const agreeRef = useRef<HTMLInputElement | null>(null);

  const clearError = (key: keyof Errors) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validateAndFocus = () => {
    const nextErrors: Errors = {};

    if (!trim(form.company)) nextErrors.company = "회사 및 단체 이름을 입력해주세요";
    if (!trim(form.representative)) {
      nextErrors.representative = "담당자 성함을 입력해주세요";
    } else if (hasNumber(form.representative)) {
      nextErrors.representative = "담당자 성함에는 숫자를 입력할 수 없습니다";
    }

    if (!trim(form.department)) nextErrors.department = "담당 부서명을 입력해주세요";

    if (!trim(form.email)) {
      nextErrors.email = "이메일을 입력해주세요";
    } else if (!isValidEmail(form.email)) {
      nextErrors.email = "이메일 형식이 올바르지 않습니다";
    }

    if (!trim(form.phone)) {
      nextErrors.phone = "휴대폰 번호를 입력해주세요";
    } else if (!isValidKoreanMobile(form.phone)) {
      nextErrors.phone = "휴대폰 번호 형식이 올바르지 않습니다";
    }

    if (!agree) nextErrors.agree = "필수 약관에 동의해주세요";

    setErrors(nextErrors);

    if (nextErrors.company) return (companyRef.current?.focus(), false);
    if (nextErrors.representative) return (representativeRef.current?.focus(), false);
    if (nextErrors.department) return (departmentRef.current?.focus(), false);
    if (nextErrors.email) return (emailRef.current?.focus(), false);
    if (nextErrors.phone) return (phoneRef.current?.focus(), false);
    // agreeRef는 Checkbox 내부 input에 ref 연결이 되어 있어야 효과가 있습니다.
    // if (nextErrors.agree) return agreeRef.current?.focus(), false;

    return true;
  };

  const onNext = () => {
    const ok = validateAndFocus();
    if (!ok) return;
    if (!agree) {
      setErrors((p) => ({ ...p, agree: "필수 약관에 동의해주세요" }));
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "btn-step1-submit",
      form_type: type === "adoption" ? "adoption" : "brochure",
    });

    if (type === "adoption") setStep(2);
    else setStep(2);
  };

  return (
    <div className="w-full md:max-w-[1012px] rounded-3xl bg-white flex flex-col gap-10 p-8 md:p-[100px] shadow-[0_4px_80px_0_rgba(0,0,0,0.1)]">
      {onTerms && (
        <TermsPopUp
          onConfirm={() => {
            setOnTerms(false);
          }}
          onCancel={() => {
            setOnTerms(false);
          }}
        />
      )}
      <div className="flex flex-col gap-10 md:gap-[50px] items-center justify-center md:items-start">
        <img width={isMobile ? 44 : 78} src="/assets/images/logo/logo_blue.png" alt="logo" />

        <div className="flex flex-col gap-5 ">
          <div className="flex flex-col gap-8 md:gap-6 items-center justify-center md:items-start">
            <img
              width={isMobile ? 44 : 66}
              src="/assets/images/icon/application.png"
              alt="application"
            />
            <h1 className="font-bold text-[24px] md:text-display text-[#121319]">
              {type === "adoption" ? "품 도입 상담 신청서" : "품 소개서 신청서"}
            </h1>
          </div>

          <span className="text-center md:text-left font-normal text-[16px] md:text-[20px] text-[#4D4F58]">
            아래 정보를 입력해주시면 {isMobile && <br />}
            {type === "adoption" ? (
              <>도입 상담을 안내해드립니다.</>
            ) : (
              '"품" 소개서를 받으실 수 있습니다. '
            )}
            {isMobile && <br />}
            {isMobile && <br />}
            {!isMobile ? <br /> : ""}
            입력하신 정보는 {isMobile && <br />}
            {type === "adoption"
              ? "상담 안내 목적으로만 사용됩니다."
              : "문의 및 소개서 발송 목적으로만 사용됩니다."}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-10 md:gap-20">
        <div className="flex flex-col gap-4 md:gap-3">
          <div className="flex flex-col gap-4">
            <InputForm
              limitLength
              label="회사·단체 이름(필수)"
              value={form.company}
              placeholder="회사·단체 이름"
              inputRef={companyRef}
              error={!!errors.company}
              errorText={errors.company}
              onChange={(v) => {
                setForm((p) => ({ ...p, company: v }));
                clearError("company");
              }}
              autoComplete="organization"
            />

            <InputForm
              limitLength
              label="담당자 성함(필수)"
              value={form.representative}
              placeholder="담당자 성함"
              inputRef={representativeRef}
              error={!!errors.representative}
              errorText={errors.representative}
              onChange={(v) => {
                //  숫자 입력 “즉시 차단” (보편적인 UX)
                const cleaned = v.replace(/\d/g, "");
                setForm((p) => ({ ...p, representative: cleaned }));
                clearError("representative");
              }}
              autoComplete="name"
            />

            <InputForm
              limitLength
              label="담당 부서(필수)"
              value={form.department}
              placeholder="담당 부서"
              inputRef={departmentRef}
              error={!!errors.department}
              errorText={errors.department}
              onChange={(v) => {
                setForm((p) => ({ ...p, department: v }));
                clearError("department");
              }}
              autoComplete="organization-title"
            />

            <InputForm
              label="업무용 이메일(필수)"
              value={form.email}
              placeholder="abc@abc.com"
              inputRef={emailRef}
              error={!!errors.email}
              errorText={errors.email}
              onChange={(v) => {
                setForm((p) => ({ ...p, email: v }));
                clearError("email");
              }}
              type="email"
              inputMode="email"
              autoComplete="email"
            />

            <InputForm
              label="휴대전화번호(필수)"
              value={form.phone}
              placeholder="01012345678"
              inputRef={phoneRef}
              error={!!errors.phone}
              errorText={errors.phone}
              onChange={(v) => {
                //  숫자만 허용
                const digitsOnly = v.replace(/\D/g, "");

                //  최대 11자리까지만 입력 허용
                if (digitsOnly.length > 11) return;

                setForm((p) => ({ ...p, phone: digitsOnly }));
                clearError("phone");
              }}
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
            />
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex flex-row gap-2 items-center px-2 -m-2 justify-between">
            <Checkbox
              checked={agree}
              label={
                <span
                  style={{
                    color: errors.agree ? "#F13221" : "#121319",
                  }}
                  className="text-[16px]"
                >
                  (필수) 개인정보 수집 및 이용동의
                </span>
              }
              onCheckedChange={() => {
                setAgree(!agree);
                clearError("agree");
              }}
            />
            <button
              onClick={() => {
                openModal("terms");
              }}
              className={`font-normal text-[14px] md:text-base  text-[#0079FF] cursor-pointer flex items-center`}
            >
              내용보기
            </button>
          </div>

          <Button
            id="btn-step1-submit"
            className={`h-14 md:h-20 text-base rounded-[12px] md:text-[28px] font-normal ${
              agree ? "bg-[#0079FF]" : "bg-[#88D0FF]"
            }`}
            size="lg"
            onClick={onNext}
          >
            다음
          </Button>
        </div>
      </div>
    </div>
  );
};
